import { useEffect, useState } from "react";
import { Bell, ArrowRight, Receipt, Calendar, Repeat } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import MoneyAmount from "@/components/MoneyAmount";

function useDueLabel() {
  const { t } = useTranslation();
  return (days) => {
    if (days < 0) return { text: t("due_overdue"), priority: "high" };
    if (days === 0) return { text: t("due_today"), priority: "high" };
    if (days === 1) return { text: t("due_tomorrow"), priority: "high" };
    if (days <= 3) return { text: t("due_in_days", { n: days }), priority: "med" };
    return { text: t("due_in_days", { n: days }), priority: "low" };
  };
}

function PriorityDot({ priority }) {
  const color =
    priority === "high" ? "#EF4444" : priority === "med" ? "#D98E36" : "#16A34A";
  return (
    <span
      aria-hidden
      className="inline-block h-2.5 w-2.5 rounded-full"
      style={{ background: color, boxShadow: `0 0 0 4px ${color}22` }}
    />
  );
}

export default function BillReminders() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const dueLabel = useDueLabel();
  const currency = user?.currency || "USD";
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/reminders/upcoming?days_ahead=7").then(({ data }) => setItems(data.upcoming || [])).catch(() => {});
  }, []);

  if (!items.length) return null;

  return (
    <div className="pcard pcard-hover p-6 md:p-7" data-testid="bill-reminders-card">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-center gap-3">
          <div
            className="flex h-11 w-11 items-center justify-center rounded-2xl text-white"
            style={{
              background: "linear-gradient(160deg, #1E3F33 0%, #4A6B53 100%)",
              boxShadow: "0 10px 22px -10px rgba(30,63,51,0.55), inset 0 1px 0 rgba(255,255,255,0.25)",
            }}
          >
            <Bell className="h-5 w-5" strokeWidth={2.2} />
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{t("coming_up")}</div>
            <div className="font-display text-xl font-bold tracking-tight">{t("bills_due_this_week")}</div>
          </div>
        </div>
        <Link
          to="/recurring"
          className="group inline-flex items-center gap-1 rounded-full border border-border bg-background/50 px-3.5 py-1.5 text-[12.5px] font-semibold text-foreground transition-all hover:border-primary/40 hover:bg-primary/5 hover:text-primary"
          data-testid="bill-reminders-link"
        >
          {t("view_all")} <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
        </Link>
      </div>

      {/* Bill list */}
      <ul className="mt-5 space-y-1.5">
        {items.slice(0, 5).map((it) => {
          const d = new Date(it.due_date);
          const today = new Date(); today.setHours(0, 0, 0, 0);
          const days = Math.round((d.getTime() - today.getTime()) / (24 * 60 * 60 * 1000));
          const { text, priority } = dueLabel(days);
          const Icon = it.is_subscription ? Repeat : Receipt;

          return (
            <li
              key={it.recurring_id + it.due_date}
              className="p-bill-row flex items-center justify-between gap-3 px-3 py-3"
              data-testid="bill-row"
            >
              <div className="flex min-w-0 items-center gap-3">
                <span
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-foreground/80"
                  style={{
                    background: "hsl(var(--muted) / 0.6)",
                    border: "1px solid hsl(var(--border) / 0.7)",
                  }}
                >
                  <Icon className="h-4.5 w-4.5" strokeWidth={2} />
                </span>
                <div className="min-w-0">
                  <div className="truncate font-semibold tracking-tight">{it.label}</div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11.5px] text-muted-foreground">
                    <span className="inline-flex items-center gap-1">
                      <PriorityDot priority={priority} /> {text}
                    </span>
                    <span aria-hidden>·</span>
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="h-3 w-3" /> {new Date(it.due_date).toLocaleDateString()}
                    </span>
                    {it.is_subscription && (
                      <>
                        <span aria-hidden>·</span>
                        <span className={`p-prio p-prio-${priority}`}>{t("subscription")}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <div className="shrink-0 text-right">
                <div className={`font-display text-base font-bold tabular-nums ${it.kind === "income" ? "text-emerald-700 dark:text-emerald-400" : "text-foreground"}`}>
                  <MoneyAmount value={it.amount} currency={currency} tone="neutral" signed={it.kind === "income"} />
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
